import { ParsedUrlQuery } from 'querystring';

import config from '../config';
import { categoryNameToUrl, encodePathSegment } from './url';

/**
 * Page routes that are alternate spellings of another page.
 *
 * A listing is served at /product/, the real page, and at /toode/, a re-export kept so
 * Estonian users keep an Estonian URL. Both return 200, so without this map each would
 * declare itself canonical and Google would treat them as competing duplicates.
 *
 * The /listing/ entry is a safety net rather than a live case: next.config.js 301s that
 * spelling and its page files are gone, so the route should never match. It is kept so
 * the mapping stays correct if that redirect is ever removed.
 */
const CANONICAL_ROUTE_MAP: Record<string, string> = {
  '/[shop-slug]/toode/[product-slug]': '/[shop-slug]/product/[product-slug]',
  '/[shop-slug]/listing/[product-slug]': '/[shop-slug]/product/[product-slug]',
};

/**
 * Route params whose value must be lowercased. Category lookup is case-insensitive, so
 * /category/Naistele and /category/naistele both resolve; feEndpoint (the form every
 * in-app link uses) is the lowercased one.
 */
const LOWERCASE_PARAMS = new Set(['category-name']);

const ROUTE_PARAM_PATTERN = /\[([^\]]+)\]/g;

const getParamValue = (query: ParsedUrlQuery, key: string) => {
  const value = query?.[key];

  return Array.isArray(value) ? value.join('/') : value;
};

/**
 * Builds the canonical path for a page from its matched route pattern and params.
 *
 * Uses router.route rather than router.asPath so the match is exact: string-replacing on
 * asPath would also rewrite sub-paths such as /toode/[slug]/muuda.
 */
export function buildCanonicalPath(route: string, query: ParsedUrlQuery, fallbackPath?: string): string {
  const canonicalRoute = CANONICAL_ROUTE_MAP[route] || route;
  let hasMissingParam = false;

  const path = canonicalRoute.replace(ROUTE_PARAM_PATTERN, (_match, key: string) => {
    const value = getParamValue(query, key);

    if (!value) {
      hasMissingParam = true;

      return '';
    }

    return encodePathSegment(LOWERCASE_PARAMS.has(key) ? categoryNameToUrl(value) : value);
  });

  // A param we cannot resolve would produce a broken canonical, so keep the current path.
  if (hasMissingParam) {
    return cleanPath(fallbackPath);
  }

  return stripTrailingSlash(path);
}

/**
 * Absolute canonical URL for a page. Used where a full URL is required (JSON-LD, og:url).
 */
export function buildCanonicalUrl(route: string, query: ParsedUrlQuery, fallbackPath?: string): string {
  return `${getBaseUrl()}${buildCanonicalPath(route, query, fallbackPath)}`;
}

export function getBaseUrl(): string {
  const country = config.get('country') as string;

  return config.get('web.meta.url')?.[country] || (typeof window !== 'undefined' ? window.location.origin : '');
}

function cleanPath(path?: string): string {
  return stripTrailingSlash((path || '').split('?')[0]);
}

function stripTrailingSlash(path: string): string {
  return path.replace(/\/+$/, '');
}
